<style lang="scss">
@import "./accounting-multi-currency-selection.scss";
</style>

<template>
  <div class="multi-currency-selection form-fields">
    <v-combobox
      class="multi-currency-selection"
      v-model="derivedModel"
      :filter="filter"
      :multiple="multiple"
      :hide-no-data="!search"
      :items="items"
      :search-input.sync="search"
      :label="label"
      :append-icon="appendIcon"
      :outline="outline"
      :item-text="multiple ? 'label' : 'value'"
      item-value="value"
      :rules="rules"
      :hide-details="hideDetails"
      :return-object="true"
      @change="updateDerivedValue"
    >
      <template v-slot:no-data>
        <v-list-tile>
          <span class="subheading">"{{ search }}" does not exist</span>
        </v-list-tile>
      </template>
      <template v-slot:selection="{ item, parent, selected }" v-if="multiple">
        <v-chip color="primary" :selected="selected" dark label small>
          <span class="pr-2">
            {{ item.value }}
          </span>
          <v-icon @click="parent.selectItem(item)">cancel</v-icon>
        </v-chip>
      </template>
      <template v-slot:item="{ item }" v-if="!multiple">
        {{ item.label }}
      </template>
    </v-combobox>
  </div>
</template>

<script>
import {
  compact,
  difference,
  filter,
  find,
  flatMap,
  includes,
  isEmpty,
  isArray,
  isObject,
  isString,
  map,
  reject,
  remove
} from "lodash";
// Constants
import { Constants } from "customer-sdk/constants";

const Currencies = Constants.CURRENCIES;
const SUPPORTED_CURRENCIES = new Currencies()
  .getData()
  .map(currency => ({ label: `${currency.code} - ${currency.name}`, value: currency.code }));

export default {
  name: "MultiCurrencySelection",
  props: {
    appendIcon: String,
    change: Function,
    commonlyUsed: Array,
    hideDetails: Boolean,
    multiple: Boolean,
    defaultItemsEnabled: Boolean,
    label: String,
    outline: Boolean,
    rules: Array,
    value: String,
    exempted: {
      type: Array,
      default: () => []
    }
  },
  data: function({ value }) {
    const component = this;
    return {
      derivedModel: component.getOptionFromValue(value),
      nonce: 1,
      search: null
    };
  },
  computed: {
    items() {
      let component = this;
      const header = { header: !isEmpty(component.commonlyUsed) ? "COMMONLY USED" : "Select a currency" };
      const supportedCurrencies = reject(SUPPORTED_CURRENCIES, currency =>
        includes(component.exempted, currency.value)
      );

      if (!isEmpty(component.commonlyUsed)) {
        const commonlyUsed = flatMap(component.commonlyUsed, commonCurrency => {
          return remove(supportedCurrencies, currency => currency.value === commonCurrency);
        });
        return [header, ...commonlyUsed, { divider: true }, ...supportedCurrencies];
      }

      return [header, ...supportedCurrencies];
    },
    currencies() {
      let component = this;
      const supportedCurrencyCodes = SUPPORTED_CURRENCIES.map(currency => currency.value);
      return difference(supportedCurrencyCodes, component.exempted);
    }
  },
  methods: {
    filter(item, queryText) {
      if (item.header) return false;
      const isIncluded = this.isCurrencyMatched(item, queryText);
      return isIncluded;
    },
    isCurrencyMatched(currencyItem, value) {
      if (isEmpty(value) || !isString(value)) {
        return false;
      }
      return currencyItem.label && includes(currencyItem.label.toUpperCase(), value.toUpperCase());
    },
    getOptionFromValue(value) {
      if (isArray(value)) {
        const options = filter(SUPPORTED_CURRENCIES, option => includes(value, option.value));
        return options;
      }
      return find(SUPPORTED_CURRENCIES, option => option.value === value);
    },
    getNewSelectOptionValues(current, availableCurrencies, component) {
      const newSelectedOptions = compact(
        map(current, selectedOption => {
          if (isString(selectedOption)) {
            const selectedQueryText = selectedOption;
            const newSelectedOption = find(availableCurrencies, option =>
              component.isCurrencyMatched(option, selectedQueryText)
            );
            return newSelectedOption;
          }
          return selectedOption;
        })
      );
      const newSelectOptionValues = map(newSelectedOptions, option => option.value);
      return { newSelectedOptions, newSelectOptionValues };
    },
    updateDerivedValue(current) {
      this.$nextTick(() => {
        let component = this;

        if (!component.multiple && isObject(current)) {
          component.$emit("input", current.value);
          component.$emit("change", current.value);
          component.derivedModel = current;
          component.search = current.value;
          return;
        }

        if (!component.multiple && isString(current) && !isEmpty(current)) {
          const availableCurrencies = reject(SUPPORTED_CURRENCIES, currency =>
            includes(component.exempted, currency.value)
          );
          const currentSelectedOption = find(availableCurrencies, option =>
            component.isCurrencyMatched(option, current)
          );

          if (!currentSelectedOption) {
            component.clearValues();
            return;
          }

          component.$emit("input", currentSelectedOption.value);
          component.$emit("change", currentSelectedOption.value);
          component.derivedModel = currentSelectedOption;
          component.search = currentSelectedOption.value;
          return;
        }

        if (component.multiple && !isEmpty(current)) {
          return component.updateMutipleValues(current, component);
        }

        component.clearValues();
      });
    },
    clearValues() {
      let component = this;
      component.$emit("input", null);
      component.$emit("change", null);
      component.derivedModel = null;
      component.search = null;
    },
    updateMutipleValues(current, component) {
      const selectedOptions = reject(current, option => isString(option));
      const selectedOptionValues = map(selectedOptions, option => option.value);
      const availableCurrencies = reject(SUPPORTED_CURRENCIES, option => {
        return includes(selectedOptionValues, option.value) || includes(component.exempted, option.value);
      });

      const { newSelectedOptions, newSelectOptionValues } = component.getNewSelectOptionValues(
        current,
        availableCurrencies,
        component
      );
      component.$emit("input", newSelectOptionValues);
      component.$emit("change", newSelectOptionValues);
      component.derivedModel = newSelectedOptions;
      component.search = newSelectOptionValues;
      return;
    }
  },
  watch: {
    exempted: function(current, previous) {
      if (this.multiple && !isEmpty(difference(current, previous))) {
        const newValues = reject(this.derivedModel, model => includes(current, model.value));
        this.$nextTick(() => this.updateMutipleValues(newValues, this));
      }
    },
    value: function(current) {
      let component = this;
      component.derivedModel = component.getOptionFromValue(current);
    }
  }
};
</script>
